/* ==========================================================================
   TUCANO · Header behaviors
   - Hover-intent for mega menu (300ms delay) + keyboard support
   - Mobile drawer toggle
   - Cart drawer trigger (delegates to Savor cart-drawer if present)
   - Search modal trigger
   ========================================================================== */

(() => {
  'use strict';

  const HOVER_OPEN_DELAY = 120;
  const HOVER_CLOSE_DELAY = 200;

  function init() {
    const header = document.querySelector('[data-tcn-header]');
    if (!header) return;

    setupMega(header);
    setupDrawer(header);
    setupSearchTrigger(header);
    setupCartTrigger(header);
    setHeaderHeightVar(header);
    setupHideOnScroll(header);
    window.addEventListener('resize', () => setHeaderHeightVar(header), { passive: true });
  }

  /* ---------------- Hide on scroll down, show on scroll up ----------------
     - Quando l'utente scrolla verso il basso (oltre una soglia) → nascondi
     - Quando scrolla verso l'alto → mostra
     - In top (scrollY < 80) → sempre visibile
     - Se un mega-menu è aperto → non nascondere (UX)
     -------------------------------------------------------------------- */
  function setupHideOnScroll(header) {
    let lastY = window.scrollY || 0;
    let ticking = false;
    const THRESHOLD = 8;       // delta minimo per evitare jitter
    const TOP_GUARD = 80;      // sopra questa soglia, sempre visibile

    const onScroll = () => {
      const y = window.scrollY || 0;
      const dy = y - lastY;

      // Mega-menu aperto → non nascondere
      const megaOpen = header.querySelector('.tcn-nav__item--has-mega.is-open');
      if (megaOpen) {
        header.classList.remove('is-hidden');
        lastY = y;
        ticking = false;
        return;
      }

      if (y < TOP_GUARD) {
        header.classList.remove('is-hidden');
      } else if (dy > THRESHOLD) {
        header.classList.add('is-hidden');     // scroll DOWN → nascondi
      } else if (dy < -THRESHOLD) {
        header.classList.remove('is-hidden');  // scroll UP → mostra
      }

      lastY = y;
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(onScroll);
        ticking = true;
      }
    }, { passive: true });
  }

  function setHeaderHeightVar(header) {
    let lastH = -1;
    const update = () => {
      const h = header.getBoundingClientRect().height;
      if (h === lastH) return; // niente ricalcolo stili se l'altezza non cambia
      lastH = h;
      header.style.setProperty('--tcn-header-h', h + 'px');
      document.documentElement.style.setProperty('--tcn-header-bottom', h + 'px');
    };
    update();
    if ('ResizeObserver' in window) {
      new ResizeObserver(update).observe(header);
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(update);
    }
  }

  /* ---------------- Mega menu ---------------- */
  function setupMega(header) {
    const items = header.querySelectorAll('[data-tcn-nav-item]');
    let openItem = null;
    let openTimer = null;
    let closeTimer = null;

    const open = (item) => {
      if (openItem === item) return;
      if (openItem) openItem.classList.remove('is-open');
      item.classList.add('is-open');
      header.classList.add('is-mega-open');
      const trigger = item.querySelector('[data-tcn-nav-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      openItem = item;
    };

    const close = (item) => {
      if (!item) return;
      item.classList.remove('is-open');
      const trigger = item.querySelector('[data-tcn-nav-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (openItem === item) {
        openItem = null;
        header.classList.remove('is-mega-open');
      }
    };

    const closeAll = () => {
      items.forEach(close);
    };

    items.forEach((item) => {
      const hasMega = item.classList.contains('tcn-nav__item--has-mega');
      if (!hasMega) return;
      const trigger = item.querySelector('[data-tcn-nav-trigger]');

      item.addEventListener('mouseenter', () => {
        clearTimeout(closeTimer);
        openTimer = setTimeout(() => open(item), HOVER_OPEN_DELAY);
      });
      item.addEventListener('mouseleave', () => {
        clearTimeout(openTimer);
        closeTimer = setTimeout(() => close(item), HOVER_CLOSE_DELAY);
      });

      if (trigger) {
        // Click toggle (mobile fallback / accessibility)
        trigger.addEventListener('click', (e) => {
          // Allow normal navigation on click — but if not yet open, prevent and open mega first
          if (window.innerWidth >= 990 && !item.classList.contains('is-open')) {
            e.preventDefault();
            closeAll();
            open(item);
          }
        });
        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            if (!item.classList.contains('is-open')) {
              e.preventDefault();
              closeAll();
              open(item);
            }
          } else if (e.key === 'Escape') {
            close(item);
            trigger.focus();
          }
        });
      }
    });

    // Close on outside click / Escape
    document.addEventListener('click', (e) => {
      if (!header.contains(e.target)) closeAll();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && openItem) {
        closeAll();
      }
    });
  }

  /* ---------------- Mobile drawer ---------------- */
  function setupDrawer(header) {
    /* Drawer è ora fuori dall'<header> per evitare il containing block
       creato dal will-change: transform sull'header → cerca nel document. */
    const drawer = document.querySelector('[data-tcn-drawer]');
    const trigger = header.querySelector('[data-tcn-drawer-trigger]');
    if (!drawer || !trigger) return;
    const closers = drawer.querySelectorAll('[data-tcn-drawer-close]');

    const open = () => {
      drawer.hidden = false;
      // Force reflow to allow transition
      drawer.offsetHeight; // eslint-disable-line no-unused-expressions
      drawer.classList.add('is-open');
      document.body.classList.add('tcn-drawer-open');
      trigger.setAttribute('aria-expanded', 'true');
      // Focus first link
      const firstFocusable = drawer.querySelector('a, button');
      if (firstFocusable) setTimeout(() => firstFocusable.focus(), 100);
    };
    const close = () => {
      drawer.classList.remove('is-open');
      document.body.classList.remove('tcn-drawer-open');
      trigger.setAttribute('aria-expanded', 'false');
      setTimeout(() => { drawer.hidden = true; }, 320);
      trigger.focus();
    };

    trigger.addEventListener('click', open);
    closers.forEach((c) => c.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !drawer.hidden) close();
    });
  }

  /* ---------------- Search modal ---------------- */
  function setupSearchTrigger(header) {
    const trigger = header.querySelector('[data-tcn-search-trigger]');
    if (!trigger) return;
    // The Tucano search modal (tucano-search.js) intercepts clicks on
    // [data-tcn-search-trigger] via document-level delegation. We only
    // need a fallback: if the modal hasn't loaded (e.g. JS error), go
    // to the search page so the user isn't stuck.
    trigger.addEventListener('click', (e) => {
      const modal = document.getElementById('tcn-search-modal');
      if (!modal) {
        // No modal in DOM → safe fallback
        e.preventDefault();
        window.location.href = '/search';
      }
      // Else let tucano-search.js handle it
    });
  }

  /* ---------------- Cart trigger ---------------- */
  function setupCartTrigger(header) {
    const trigger = header.querySelector('[data-tcn-cart-trigger]');
    if (!trigger) return;
    trigger.addEventListener('click', (e) => {
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (!cartDrawer) return; // fallback: let link navigate to /cart

      e.preventDefault();
      // Savor's API: web component exposes .open()
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        return;
      }
      // Fallback: find internal <dialog ref="dialog"> and showModal
      const dialog = cartDrawer.querySelector('dialog[ref="dialog"], dialog.cart-drawer__dialog, dialog');
      if (dialog && typeof dialog.showModal === 'function') {
        dialog.showModal();
        return;
      }
      // Last resort: click Savor's internal trigger if present
      const innerTrigger = cartDrawer.querySelector('[data-testid="cart-drawer-trigger"]');
      if (innerTrigger) innerTrigger.click();
    });

    // Listen for Savor cart events (CartAddEvent + CartUpdateEvent both dispatch 'cart:update')
    document.addEventListener('cart:update', (e) => {
      const count =
        e?.detail?.resource?.item_count ??
        e?.detail?.data?.itemCount ??
        null;
      if (count !== null) updateCartCount(header, count);
      else fetchCartCount(header);
    });
    // Safety net for any other variant/legacy event names
    document.addEventListener('cart:updated', (e) => {
      updateCartCount(header, e?.detail?.cart?.item_count);
    });
  }

  function updateCartCount(header, count) {
    const el = header.querySelector('[data-tcn-cart-count]');
    if (!el) return;
    if (typeof count !== 'number') return;
    el.textContent = String(count);
    if (count > 0) el.removeAttribute('hidden');
    else el.setAttribute('hidden', '');
  }

  function fetchCartCount(header) {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.ok ? r.json() : null)
      .then((cart) => { if (cart) updateCartCount(header, cart.item_count); })
      .catch(() => {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
